import React, { useState, useEffect } from "react";
import { useQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import backArrow from "assets/back-arrow.svg";
import { ENROLLMENT_STATUS } from "utils/constants";
import { VerificationExplanation } from "./Explanation";
import { VerificationPersonalInfo } from "./PersonalInfo";
import { VerificationQuestionnaire } from "./Questionnaire";
import { VerificationSuccess } from "./Success";

const VERIFICATION_STEP = {
  PERSONAL_INFO: "PERSONAL_INFO",
  QUESTIONNAIRE: "QUESTIONNAIRE",
  OTP: "OTP",
  SUCCESS: "SUCCESS",
};

const USER_DETAILS_QUERY = gql`
  query UserDetails {
    user {
      first_name
      last_name
      dob
      email
      phone_number
      ssn_last_four
      billing_address {
        street
        city
        state
        zip
        unit_number
      }
    }
  }
`;

const stripCountryCode = (phone = "") => {
  if (phone?.startsWith("+1")) {
    return phone.substring(2);
  }
  return phone;
};

/**
 * @param {Object} props
 * @param {Function} props.setStatus
 * @returns
 */
export const CreditMonitoringVerification = (props) => {
  const { setStatus } = props;
  const { data: userData, loading: userLoading } = useQuery(USER_DETAILS_QUERY);
  const [steps, setSteps] = useState([VERIFICATION_STEP.PERSONAL_INFO]); // stack of steps
  const [backButtonVisible, setBackButtonVisible] = useState(true);
  const [disabledFields, setDisabledFields] = useState([]);

  const [userInfo, setUserInfo] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: moment().format("MM/DD/YYYY"),
    ssn: "",
    phoneNumber: "",
    address: {
      street: "",
      unit: "",
      city: "",
      state: "",
      zip: "",
    },
  });

  useEffect(() => {
    if (userData && userData.user) {
      const { user = {} } = userData;
      const {
        first_name: _firstName,
        last_name: _lastName,
        dob,
        phone_number: _phoneNumber,
        billing_address: billingAddress = {},
        ssn_last_four: _ssn,
      } = user;
      const {
        street,
        city,
        state,
        unit_number: unit,
        zip,
      } = billingAddress || {};
      const prefilledUser = {
        firstName: _firstName || "",
        lastName: _lastName || "",
        dateOfBirth: dob ? moment(dob).format("MM/DD/YYYY") : null,
        phoneNumber: _phoneNumber ? stripCountryCode(_phoneNumber) : "",
        ssn: _ssn ? "123456789" : "", // keep the default value for ssn to pass the validation
        address: {
          street: street || "",
          unit: unit || "",
          city: city || "",
          state: state || "",
          zip: zip || "",
        },
      };
      const _disabledFields = [];
      Object.keys(prefilledUser).forEach((key) => {
        if (prefilledUser[key]) {
          _disabledFields.push(key);
        }
      });
      if (Object.keys(prefilledUser.address).some((key) => !!prefilledUser.address[key])) {
        // if any address field is prefilled, disable all address fields
        _disabledFields.push("street", "city", "zip", "state", "unit");
      }
      setDisabledFields([..._disabledFields]);
      setUserInfo(prefilledUser);
    }
  }, [userData]); // Only re-run the effect if userData changes

  const currentStep = steps.at(-1);

  const handleBack = () => {
    if (steps.length === 1) {
      setStatus(ENROLLMENT_STATUS.ELIGIBLE);
    }
    const newSteps = steps.slice(0, -1);
    setSteps([...newSteps]);
  };

  const onChangeField = (field, value) => {
    setUserInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const renderCurrentStep = () => {
    if (currentStep === VERIFICATION_STEP.PERSONAL_INFO) {
      return (
        <div className="mt-6">
          <VerificationPersonalInfo
            onContinue={() =>
              setSteps([...steps, VERIFICATION_STEP.QUESTIONNAIRE])
            }
            onChangeField={onChangeField}
            userInfo={userInfo}
            userLoading={userLoading}
            disabledFields={disabledFields}
          />
        </div>
      );
    }
    if (currentStep === VERIFICATION_STEP.QUESTIONNAIRE) {
      return (
        <div className="mt-6">
          <VerificationQuestionnaire
            onContinue={() => {
              setSteps([...steps, VERIFICATION_STEP.SUCCESS]);
              setBackButtonVisible(false);
            }}
            onLockedOut={() => setBackButtonVisible(false)}
          />
        </div>
      );
    }
    if (currentStep === VERIFICATION_STEP.SUCCESS) {
      return (
        <div>
          <VerificationSuccess />
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col w-full h-full md:flex-row">
      <div className="flex-1 flex-col p-6 md:p-10">
        {
          backButtonVisible && (
            <button
              type="button"
              className="flex items-center"
              style={{ outline: "none" }}
              onClick={handleBack}
            >
              <img src={backArrow} alt="back-arrow" />
              <p className="ml-1 font-medium text-base">Back</p>
            </button>
          )
        }
        {renderCurrentStep()}
      </div>
      <div className="hidden md:flex justify-center bg-green-secondary rounded-lg flex-1 px-20 pt-24">
        <VerificationExplanation />
      </div>
    </div>
  );
};
